/**
 * **BarneoProductsWidget** - Компонент отображения товаров
 * 
 * Компонент предоставляет интерфейс для отображения товаров с пагинацией,
 * адаптивной сеткой и кастомными слотами.
 * 
 * @component
 * @example
 * ```vue
 * <template>
 *   <barneo-products-widget 
 *     :products="products"
 *     :loading="isLoading"
 *     @product-selected="handleProductSelect"
 *     @page-changed="handlePageChange"
 *   />
 * </template>
 * 
 * <script setup>
 * import { ref } from 'vue'
 * 
 * const products = ref([])
 * const isLoading = ref(false)
 * 
 * const handleProductSelect = (product) => {
 *   console.log('Товар выбран:', product)
 * }
 * 
 * const handlePageChange = (page) => {
 *   console.log('Страница изменена:', page)
 * }
 * </script>
 * ```
 * 
 * @see {@link BarneoProductsWidgetProps} - Props компонента
 * @see {@link BarneoProductsWidgetEmits} - Events компонента
 * @see {@link BarneoProductsWidgetSlots} - Slots компонента
 * @see {@link ProductsWidgetConfig} - Конфигурация компонента
 * @see {@link Product} - Тип товара
 * @see {@link PaginationState} - Состояние пагинации
 */

import type { BarneoProductsWidgetProps, ProductsWidgetEmits } from './components'
import type { BarneoProductsWidgetSlots } from './slots'
import type { ProductsWidgetConfig, Product, PaginationState } from './models'

/**
 * Props компонента BarneoProductsWidget
 * 
 * @interface BarneoProductsWidgetProps
 * @description Все доступные props для компонента товаров
 * 
 * @property {Product[]} [products] - Список товаров для отображения
 * @property {boolean} [loading] - Состояние загрузки
 * @property {string | null} [error] - Текст ошибки
 * @property {PaginationState} [pagination] - Состояние пагинации
 * @property {ProductsWidgetConfig} [config] - Локальная конфигурация
 * @property {SearchApiService} [apiService] - Кастомный API сервис
 * 
 * @example
 * ```vue
 * <barneo-products-widget
 *   :products="productsList"
 *   :loading="isLoading"
 *   :error="errorMessage"
 *   :pagination="paginationState"
 *   :config="productsConfig"
 * />
 * ```
 */
export type { BarneoProductsWidgetProps }

/**
 * Events компонента BarneoProductsWidget
 * 
 * @interface ProductsWidgetEmits
 * @description Все доступные события компонента товаров
 * 
 * @property {(product: Product) => void} productSelected - Выбран товар
 * @property {(page: number) => void} pageChanged - Изменена страница
 * @property {(isLoading: boolean) => void} loading - Изменение состояния загрузки
 * @property {(error: string) => void} error - Произошла ошибка
 * 
 * @example
 * ```vue
 * <barneo-products-widget
 *   @product-selected="handleProductSelect"
 *   @page-changed="handlePageChange"
 *   @loading="handleLoading"
 *   @error="handleError"
 * />
 * ```
 */
export type { ProductsWidgetEmits }

/**
 * Slots компонента BarneoProductsWidget
 * 
 * @interface BarneoProductsWidgetSlots
 * @description Все доступные слоты для кастомизации компонента
 * 
 * @property {Object} productsGrid - Кастомная сетка товаров
 * @property {Object} productCard - Кастомная карточка товара
 * @property {Object} productHeader - Заголовок карточки товара (метаданные + избранное)
 * @property {Object} productMetadata - Метаданные товара (код, артикул)
 * @property {Object} productFavourites - Кнопка избранного
 * @property {Object} productImage - Изображение товара
 * @property {Object} productInfo - Информационный блок товара (цена, бренд, наличие)
 * @property {Object} productPrice - Цена товара
 * @property {Object} productBrand - Бренд товара
 * @property {Object} productAvailability - Наличие товара
 * @property {Object} productActions - Блок действий товара (название, доставка, кнопка)
 * @property {Object} productTitle - Название товара
 * @property {Object} productDelivery - Информация о доставке
 * @property {Object} productAddToCart - Кнопка добавления в корзину
 * @property {Object} productsEmpty - Пустое состояние
 * @property {Object} productsPagination - Кастомная пагинация
 * @property {Object} loading - Состояние загрузки
 * @property {Object} error - Состояние ошибки
 * 
 * @example
 * ```vue
 * <barneo-products-widget>
 *   <!-- Кастомная карточка товара -->
 *   <template #product-card="{ product, index, onSelect }">
 *     <div class="custom-product-card" @click="onSelect(product)">
 *       <img :src="product.picture" :alt="product.name" />
 *       <h3>{{ product.name }}</h3>
 *       <p class="price">{{ formatPrice(product.price) }}</p>
 *     </div>
 *   </template>
 *   
 *   <!-- Кастомный заголовок карточки -->
 *   <template #product-header="{ product, isInFavourites, toggleFavourite }">
 *     <div class="custom-header">
 *       <span class="product-code">{{ product.vendor_code }}</span>
 *       <button 
 *         :class="{ 'favourite': isInFavourites }"
 *         @click.stop="toggleFavourite()"
 *       >
 *         ❤️
 *       </button>
 *     </div>
 *   </template>
 *   
 *   <!-- Кастомная цена -->
 *   <template #product-price="{ product, formatPrice }">
 *     <div class="custom-price">
 *       <span class="currency">₽</span>
 *       <span class="amount">{{ formatPrice(product.price) }}</span>
 *     </div>
 *   </template>
 *   
 *   <!-- Кастомная кнопка добавления в корзину -->
 *   <template #product-add-to-cart="{ product, addToCart, isDisabled }">
 *     <button 
 *       class="custom-add-to-cart"
 *       :disabled="isDisabled"
 *       @click.stop="addToCart()"
 *     >
 *       {{ isDisabled ? 'Нет в наличии' : 'Купить' }}
 *     </button>
 *   </template>
 *   
 *   <!-- Кастомная пагинация -->
 *   <template #products-pagination="{ pagination, onPageChange }">
 *     <div class="custom-pagination">
 *       <button 
 *         v-for="page in pagination.totalPages" 
 *         :key="page"
 *         :class="{ active: page === pagination.currentPage }"
 *         @click="onPageChange(page)"
 *       >
 *         {{ page }}
 *       </button>
 *     </div>
 *   </template>
 * </barneo-products-widget>
 * ```
 */
export type { BarneoProductsWidgetSlots }

/**
 * Конфигурация компонента товаров
 * 
 * @interface ProductsWidgetConfig
 * @description Настройки компонента товаров
 * 
 * @property {number} [itemsPerPage] - Количество товаров на странице
 * @property {number} [mobileItemWidth] - Ширина элемента на мобильных
 * @property {number} [desktopItemWidth] - Ширина элемента на десктопе
 * @property {boolean} [darkMode] - Принудительная темная тема
 * @property {boolean} [showPagination] - Показывать пагинацию
 * @property {boolean} [showTotalCount] - Показывать общее количество
 * 
 * @example
 * ```typescript
 * const config: ProductsWidgetConfig = {
 *   itemsPerPage: 20,
 *   mobileItemWidth: 160,
 *   desktopItemWidth: 200,
 *   darkMode: false,
 *   showPagination: true,
 *   showTotalCount: true
 * }
 * ```
 */
export type { ProductsWidgetConfig }

/**
 * Товар
 * 
 * @interface Product
 * @description Структура товара
 * 
 * @property {string} id - Уникальный идентификатор товара
 * @property {string} name - Название товара
 * @property {string} [description] - Описание товара
 * @property {number} price - Цена товара
 * @property {string | null} picture - URL изображения товара
 * @property {string | null} url - URL страницы товара
 * @property {string} vendor_code - Артикул товара
 * @property {Array} properties - Свойства товара
 * @property {Object} [category] - Категория товара
 * @property {Array} [classification_properties] - Классификационные свойства
 * 
 * @example
 * ```typescript
 * const product: Product = {
 *   id: '123',
 *   name: 'Сковорода Tefal',
 *   description: 'Антипригарная сковорода',
 *   price: 2500,
 *   picture: 'https://example.com/image.jpg',
 *   url: 'https://example.com/product/123',
 *   vendor_code: 'TEF-001',
 *   properties: [],
 *   category: { id: '1', name: 'Посуда' }
 * }
 * ```
 */
export type { Product }

/**
 * Состояние пагинации
 * 
 * @interface PaginationState
 * @description Структура состояния пагинации
 * 
 * @property {number} currentPage - Текущая страница
 * @property {number} totalPages - Общее количество страниц
 * @property {number} totalItems - Общее количество товаров
 * @property {number} itemsPerPage - Количество товаров на странице
 * @property {boolean} [hasNextPage] - Есть ли следующая страница
 * @property {boolean} [hasPrevPage] - Есть ли предыдущая страница
 * 
 * @example
 * ```typescript
 * const pagination: PaginationState = {
 *   currentPage: 1,
 *   totalPages: 5,
 *   totalItems: 100,
 *   itemsPerPage: 20,
 *   hasNextPage: true,
 *   hasPrevPage: false
 * }
 * ```
 */
export type { PaginationState }

/**
 * Утилиты для работы с товарами
 * 
 * @function formatPrice
 * @description Форматирует цену в читаемый вид
 * 
 * @param {number} price - Цена товара
 * @param {string} [currency] - Валюта (по умолчанию: '₽')
 * @returns {string} Отформатированная цена
 * 
 * @example
 * ```typescript
 * const formattedPrice = formatPrice(2500) // "2 500 ₽"
 * const formattedPriceUSD = formatPrice(100, '$') // "100 $"
 * ```
 */
export function formatPrice(price: number, currency: string = '₽'): string {
  return `${price.toLocaleString('ru-RU')} ${currency}`
}

/**
 * Утилиты для работы с изображениями
 * 
 * @function handleImageError
 * @description Обрабатывает ошибки загрузки изображений
 * 
 * @param {Event} event - Событие ошибки
 * @param {string} [fallbackSrc] - URL изображения по умолчанию
 * 
 * @example
 * ```typescript
 * const handleError = (event) => {
 *   handleImageError(event, '/images/no-image.png')
 * }
 * ```
 */
export function handleImageError(event: Event, fallbackSrc: string = '/images/no-image.png'): void {
  const target = event.target as HTMLImageElement
  if (target) {
    target.src = fallbackSrc
    target.alt = 'Изображение недоступно'
  }
}

/**
 * Связанные файлы и типы:
 * 
 * - {@link BarneoProductsWidget} - Сам компонент
 * - {@link useProductsWidget} - Composable для товаров
 * - {@link ProductsState} - Состояние товаров
 * - {@link ProductsRequest} - Запрос товаров к API
 * - {@link ProductsResponse} - Ответ API товаров
 * 
 * @see {@link https://github.com/denis-muhortov/barneo-search-widget-lib} - Документация библиотеки
 */
